import { useDndContext, useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Button,
  IconButton,
  Paper,
  Popover,
  Theme,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  ExerciseForm,
  Metric,
  getYoutubePreviewThumbnail,
  getYoutubeVideoId,
} from '@strength/domain';
import {
  HasAthleteWorkoutLayer,
  Layer,
  MetricBenchmarkData,
  WorkoutAlternateGroup,
  WorkoutExercise,
  WorkoutPublishErrors,
  WorkoutSection,
  WorkoutSuperset,
  getDefaultSetMetrics,
  getExerciseDisplay,
} from '@strength/domain-workout';
import { nextSortOrder, sortByOrder } from '@strength/utils';
import {
  MdAdd,
  MdContentCopy,
  MdDragIndicator,
  MdForkRight,
  MdKeyboardArrowDown,
  MdOutlineNote,
  MdPlayCircle,
  MdSubdirectoryArrowRight,
  MdToday,
} from '@teamworksdev/icons';
import { useAtom, useAtomValue } from 'jotai';
import { MouseEvent, ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { trackEvent } from '../../../../reporting/reporting';
import { trpc } from '../../../../trpc/react';
import { SupersetIcon } from '../../../components/icons/SupersetIcon';
import { VideoPreviewPlayer } from '../../../components/multimedia/video-preview-player';
import { CELL_CLASSNAME_SELECTED } from '../../../components/sheet';
import { CustomSlot } from '../../../components/sheet/slots/custom-slot';
import { MetricsSlot } from '../../../components/sheet/slots/metrics-slot';
import {
  ADD_EXERCISE_SLOT,
  ADD_SET_SLOT,
  EXERCISE_NAME_SLOT,
} from '../../../components/sheet/slots/slot-labels';
import { cn } from '../../../components/utils';
import { useViewer } from '../../../context/viewer';
import { DisabledWithTooltip } from '../../../components/permissions/DisabledWithTooltip';
import { toast } from '../../../hooks/useToast';
import { jotaiStore } from '../../../utils/jotai';
import { CreateEditExerciseSheet } from '../../library/exercises/components/CreateEditExerciseSheet';
import { createExerciseInput } from '../../library/exercises/components/exercises';
import { AdvancedExerciseSearchModal } from '../advanced-search/AdvancedExerciseSearchModal';
import { AdvancedSchemeSearchModal } from '../advanced-search/AdvancedSchemeSearchModal';
import {
  ApplySwapCallbacks,
  ApplySwapData,
  findRecentSwap,
  recentSwapsAtom,
} from '../components/apply-swap-drawer';
import {
  DROPPABLE_CLASSNAME,
  WorkoutExerciseDraggable,
  WorkoutExerciseDroppable,
  exerciseDndStyleInfo,
} from '../dnd';
import {
  MiscMutationFns,
  WorkoutLayerMutationFns,
} from '../mutation-fns/types';
import {
  BlockBuilderExercise,
  BlockSelectionData,
  BlockSlot,
  Scheme,
} from '../types';
import { isLastExerciseInGroup } from '../utils';
import { CollapseSlotIcon } from './CollapseSlotIcon';
import { useBlockExercises } from '../hooks/useBlockExercises';
import { ExerciseTagChips } from './ExerciseTagChips';
import { getAlternateExerciseSearchParams } from './alternate-exercise-search';
import { rankExercises } from './exercise-search-ranking';
import {
  SlotsResults,
  blockSectionAndExerciseLabelStyle,
  buildSwapExerciseArgs,
  collapsedExercisesAtom,
  exerciseVideoMapAtom,
  inputAtom,
} from './common';
import { SetMetricCellContent } from './sets';
import { WorkoutColumnInfo } from './workout';

export function exerciseColumnCount(
  exercise: WorkoutExercise,
  isReadOnly = false,
  hasAlternateGroup = false,
) {
  if (isReadOnly) {
    // 2 = LibraryExercise name and fields
    return 2 + exercise.sets.length;
  }
  // LibraryExercise name, fields, and if there's metrics show metrics and add set
  const hasMetrics = exercise.metrics.length > 0;
  return (
    1 +
    (hasMetrics ? 2 : 0) +
    (hasAlternateGroup ? 1 : 0) +
    exercise.sets.length
  );
}

export function getExerciseSlots(
  section: WorkoutSection,
  superset: WorkoutSuperset | null,
  alternateGroup: WorkoutAlternateGroup | null,
  exercise: WorkoutExercise,
  isCollapsed: boolean,
  exerciseKey: string,
  errors: WorkoutPublishErrors | null,
  x: number,
  y: number,
  workoutWidth: number,
  disabled: boolean,
  mutationFns: WorkoutLayerMutationFns,
  miscMutationFns: MiscMutationFns,
  applyBenchmarkData: MetricBenchmarkData | undefined,
  setModalOrDrawerOpen: (open: boolean) => void,
  getMetric: (id: string) => Metric,
  actuals?: HasAthleteWorkoutLayer,
  workoutColumnInfo?: WorkoutColumnInfo,
  gridOptions?: {
    style?: React.CSSProperties;
    overrideGridStyles?: boolean;
  },
  showActuals?: boolean,
  supersetIndexMap?: Map<string, number>,
  applySwapCallbacks?: ApplySwapCallbacks,
): SlotsResults {
  const onAddSet = () => {
    mutationFns.onAddSet(
      section.workoutId,
      exercise.workoutExerciseId,
      {
        sortOrder: nextSortOrder(exercise.sets),
      },
      getDefaultSetMetrics(exercise.metrics),
    );
  };

  const onSwap = (swapExercise: BlockBuilderExercise) => {
    const args = buildSwapExerciseArgs(
      section,
      exercise,
      superset,
      alternateGroup,
      swapExercise,
    );

    mutationFns.onSwapExercise(
      args.workoutId,
      args.workoutSectionId,
      args.previousWorkoutExerciseId,
      args.data,
      args.metrics,
      args.sets,
      args.superset,
      args.alternateGroup,
    );

    // Notify the sheet so it can check eligibility and track if applicable
    applySwapCallbacks?.onTrackExerciseSwap({
      workoutId: section.workoutId,
      exerciseId: exercise.workoutExerciseId,
      oldExerciseName: exercise.exerciseName,
      newExerciseName: swapExercise.name,
      oldLibraryExerciseId: exercise.libraryExerciseId,
      newLibraryExerciseId: swapExercise.id,
    });
  };

  const slots: BlockSlot[] = [];
  let rowCount = 0;

  const altExercise = alternateGroup?.exercises.find(
    (a) => a.workoutExerciseId === exercise.workoutExerciseId,
  );
  slots.push(
    new CustomSlot(
      { x, y: y + rowCount },
      workoutWidth,
      1,
      disabled,
      (editing, onBlur) => (
        <ExerciseLabel
          disabled={disabled}
          superset={superset}
          alternateGroup={alternateGroup}
          exercise={exercise}
          workoutId={section.workoutId}
          supersetIndexMap={supersetIndexMap}
          errors={errors}
          onSelectScheme={(scheme) =>
            miscMutationFns.onSelectScheme(section, exercise, scheme)
          }
          setModalOrDrawerOpen={setModalOrDrawerOpen}
          editing={editing}
          onBlur={onBlur}
          onSwap={(exercise) => onSwap(exercise)}
          onOpenApplySwap={applySwapCallbacks?.onOpenApplySwap}
          isAlternate={!!altExercise && altExercise?.sortOrder !== 0}
          exerciseKey={exerciseKey}
        />
      ),
      {
        data: [
          {
            type: 'Exercise',
            workoutId: section.workoutId,
            exercise: exercise,
            section,
            superset,
            disabled: disabled,
            alternateGroup,
          },
        ],
        editBehavior: {
          type: 'input',
          onStart: (input) =>
            jotaiStore.set(inputAtom, input ?? exercise.exerciseName),
          onCommit: () => {},
        },
        style: gridOptions?.style,
        overrideGridStyles: gridOptions?.overrideGridStyles,
      },
      EXERCISE_NAME_SLOT,
    ),
  );
  rowCount += 1;

  if (exercise.metrics.length !== 0 && !isCollapsed) {
    // When the Sets column is hidden, drop the Sets metric so the metric count
    // matches workoutWidth (which excludes it) — otherwise the trailing metric
    // is pushed outside the slot region and cut off.
    const showsSetsColumn = workoutColumnInfo?.shouldShowSetsColumn ?? true;
    const visibleMetrics = showsSetsColumn
      ? exercise.metrics
      : exercise.metrics.filter((m) => m.metricType !== 'sets');
    const hiddenColumnsCount = workoutWidth - visibleMetrics.length;
    slots.push(
      new MetricsSlot(
        disabled,
        () => disabled,
        { x, y: y + rowCount },
        sortByOrder(visibleMetrics),
        exercise.sets,
        (edit, set) => {
          const metric = set.metrics.find((m) => m.metricId === edit.metricId);
          const curValue = metric?.input;
          const newValue = edit.input;
          if (curValue === newValue) {
            return;
          }

          mutationFns.onEditSetMetric(section.workoutId, set.workoutSetId, {
            metricId: edit.metricId,
            input: newValue,
          });
        },
        inputAtom,
        (props) => {
          // Find the actuals data for this metric, if any
          const metric = Layer(actuals).setMetric({
            workoutSetId: props.row.workoutSetId,
            metricId: props.metric.metricId,
          });
          const actualSetMetric = metric
            ? {
                metricId: metric.metricId,
                input: metric.input ?? '',
                forCollection: metric.forCollection ?? false,
                dataSource: metric.dataSource,
              }
            : undefined;
          return (
            <SetMetricCellContent
              {...props}
              context={{
                ...props.context,
                getMetric,
                actualSetMetric,
                profileId: actuals?.profileId,
              }}
            />
          );
        },
        {
          workoutErrors: errors,
          applyBenchmarkData,
          libraryExerciseId: exercise.libraryExerciseId,
        },
        0, // gridSubRows isn't used when we override the grid template columns
        hiddenColumnsCount,
        getMetric,
        {
          libraryExerciseId: exercise.libraryExerciseId,
          rawDataFn: (
            rows,
            rowMetrics,
            headerMetrics,
          ): BlockSelectionData[] => [
            ...rows.map((set) => ({
              workoutId: section.workoutId,
              type: 'Set' as const,
              set,
              metrics: rowMetrics,
              exercise,
              disabled,
            })),
            ...headerMetrics.map((metric) => ({
              workoutId: section.workoutId,
              type: 'ExerciseMetric' as const,
              metric,
              exercise,
              disabled,
            })),
          ],
        },
        undefined, // className
        {
          workoutExerciseId: exercise.workoutExerciseId,
          workoutSectionId: section.workoutSectionId,
          workoutId: section.workoutId,
        },
        workoutColumnInfo,
        showActuals,
      ),
    );
    rowCount += exercise.sets.length + 1;
  }

  if (!disabled && exercise.metrics.length !== 0 && !isCollapsed) {
    slots.push(
      new CustomSlot(
        { x, y: y + rowCount },
        workoutWidth,
        1,
        false,
        () => <AddSetButton onClick={onAddSet} />,
        {
          data: [
            {
              workoutId: section.workoutId,
              type: 'AddSet',
              exercise,
            },
          ],
          editBehavior: {
            type: 'trigger',
            onEnter: onAddSet,
          },
          className: 'p-0',
          style: gridOptions?.style,
          overrideGridStyles: gridOptions?.overrideGridStyles,
        },
        ADD_SET_SLOT,
      ),
    );
    rowCount += 1;
  }

  if (
    isLastExerciseInGroup(alternateGroup, exercise.workoutExerciseId) &&
    !isCollapsed
  ) {
    // The user has clicked the "Add Alternate Exercise" option, so expose an add exercise button
    slots.push(
      ...getAddExerciseSlot(
        section,
        x,
        y + rowCount,
        workoutWidth,
        disabled,
        // TODO
        mutationFns,
        undefined,
        alternateGroup?.workoutAlternateGroupId,
        gridOptions,
        alternateGroup,
      ).slots,
    );
    rowCount += 1;
  }

  return { slots, rowCount };
}

export function getAddExerciseSlot(
  section: WorkoutSection,
  x: number,
  y: number,
  workoutWidth: number,
  disabled: boolean,
  mutationFns: WorkoutLayerMutationFns,
  setModalOrDrawerOpen?: (open: boolean) => void,
  altGroupId?: string,
  gridOptions?: {
    style?: React.CSSProperties;
    overrideGridStyles?: boolean;
  },
  // Present only for the add-alternate-exercise button; drives overlap ranking,
  // group exclusion, and inline tag chips in the picker.
  alternateGroup?: WorkoutAlternateGroup | null,
): SlotsResults {
  const slot: BlockSlot = new CustomSlot(
    { x, y },
    workoutWidth,
    1,
    false,
    (editing, onBlur) => (
      <AddExercise
        disabled={disabled}
        onBlur={onBlur}
        editing={editing}
        onAdd={(exercise) => {
          mutationFns.onAddExercise(
            section.workoutId,
            section.workoutSectionId,
            {
              libraryExerciseId: exercise.id,
              exerciseName: exercise.name,
              instruction: null,
              sortOrder: nextSortOrder(
                section.entries.flatMap((e) =>
                  e.__type === 'WorkoutExercise'
                    ? e
                    : e.exercises.map((ee) => ee.exercise),
                ),
              ),
              isEachSide: false,
            },
            exercise.defaultMetrics.map(({ metric, sortOrder }) => ({
              id: v4(),
              workoutExerciseMetricId: v4(),
              metricId: metric.id,
              metricName: metric.name,
              metricType: metric.type,
              sortOrder,
            })),
            undefined,
            altGroupId,
          );
        }}
        setModalOrDrawerOpen={setModalOrDrawerOpen}
        workoutSectionId={section.workoutSectionId}
        workoutId={section.workoutId}
        isAlternate={!!altGroupId}
        alternateGroup={alternateGroup}
      />
    ),
    {
      data: [
        {
          type: 'AddExercise',
          workoutId: section.workoutId,
          section,
          altGroupId,
        },
      ],
      editBehavior: {
        type: 'input',
        onStart: (input) => jotaiStore.set(inputAtom, input ?? ''),
        onCommit: () => {},
      },
      style: gridOptions?.style,
      overrideGridStyles: gridOptions?.overrideGridStyles,
    },
    ADD_EXERCISE_SLOT,
  );

  return {
    slots: [slot],
    rowCount: 1,
  };
}

type AdvancedSearchState = { searchTerm: string; open: boolean };

function ExerciseLabel({
  disabled,
  superset,
  alternateGroup,
  exercise,
  workoutId,
  supersetIndexMap,
  onSelectScheme,
  errors,
  editing,
  onBlur,
  onSwap,
  onOpenApplySwap,
  setModalOrDrawerOpen,
  isAlternate,
  exerciseKey,
}: {
  disabled: boolean;
  superset: WorkoutSuperset | null;
  alternateGroup: WorkoutAlternateGroup | null;
  exercise: WorkoutExercise;
  workoutId: string;
  supersetIndexMap?: Map<string, number>;
  onSelectScheme: (scheme: Scheme) => void;
  onSwap: (exercise: BlockBuilderExercise) => void;
  onOpenApplySwap?: (data: ApplySwapData) => void;
  errors: WorkoutPublishErrors | null;
  editing: boolean;
  onBlur: () => void;
  setModalOrDrawerOpen: (open: boolean) => void;
  isAlternate: boolean;
  exerciseKey: string;
}) {
  const { team } = useViewer();
  const onCreateExercise = useCreateExercise(onSwap);

  const [advancedExerciseSearch, setAdvancedExerciseSearch] =
    useState<AdvancedSearchState>({ searchTerm: '', open: false });
  const [advancedSchemeSearch, setAdvancedSchemeSearch] =
    useState<AdvancedSearchState>({ searchTerm: '', open: false });
  const [createExerciseSheetOpen, setCreateExerciseSheetOpen] = useState(false);
  const [exerciseSearchTerm, setExerciseSearchTerm] = useState<string>('');

  if (editing && !disabled) {
    return (
      <ExerciseSearch
        onBlur={onBlur}
        onSelect={onSwap}
        onOpenAdvancedSearch={(term) => {
          setModalOrDrawerOpen?.(true);
          setAdvancedExerciseSearch({ searchTerm: term, open: true });
        }}
        setCreateExerciseSheetOpen={(searchTerm) => {
          setModalOrDrawerOpen?.(true);
          setCreateExerciseSheetOpen(true);
          setExerciseSearchTerm(searchTerm);
        }}
      />
    );
  }

  return (
    <>
      <ExerciseLabelDraggable
        disabled={disabled}
        exercise={exercise}
        superset={superset}
        alternateGroup={alternateGroup}
        workoutId={workoutId}
        supersetIndexMap={supersetIndexMap}
        errors={errors}
        onSelectScheme={onSelectScheme}
        setAdvancedSchemeSearchOpen={() =>
          setAdvancedSchemeSearch({ searchTerm: '', open: true })
        }
        onOpenApplySwap={onOpenApplySwap}
        isAlternate={isAlternate}
        exerciseKey={exerciseKey}
      />
      {advancedExerciseSearch.open && (
        <AdvancedExerciseSearchModal
          id={exercise.workoutExerciseId}
          initialSearchTerm={advancedExerciseSearch.searchTerm}
          open={advancedExerciseSearch.open}
          onClose={() => {
            setModalOrDrawerOpen?.(false);
            setAdvancedExerciseSearch({ searchTerm: '', open: false });
          }}
          setOpenCreateExerciseSheet={(searchTerm) => {
            setCreateExerciseSheetOpen(true);
            setAdvancedExerciseSearch({ searchTerm: '', open: false });
            setExerciseSearchTerm(searchTerm);
          }}
          onAddExercise={(exercise) => {
            onSwap(exercise);
            setAdvancedExerciseSearch({ searchTerm: '', open: false });
            setModalOrDrawerOpen?.(false);
          }}
          teamId={team.selectedTeam}
        />
      )}
      {advancedSchemeSearch.open && (
        <AdvancedSchemeSearchModal
          exerciseId={exercise.libraryExerciseId}
          open={advancedSchemeSearch.open}
          onClose={() => {
            setModalOrDrawerOpen?.(false);
            setAdvancedSchemeSearch({ searchTerm: '', open: false });
          }}
          onAddScheme={(scheme) => {
            onSelectScheme(scheme);
            setAdvancedSchemeSearch({ searchTerm: '', open: false });
          }}
        />
      )}

      <CreateEditExerciseSheet
        open={createExerciseSheetOpen}
        onOpenChange={() => {
          setModalOrDrawerOpen?.(false);
          setCreateExerciseSheetOpen(false);
          setExerciseSearchTerm('');
        }}
        onSubmit={onCreateExercise}
        defaultValues={{ name: exerciseSearchTerm }}
        teamId={team.selectedTeam}
        showTeamName={true}
      />
    </>
  );
}

const ExerciseLabelDraggable = ({
  disabled,
  exercise,
  superset,
  alternateGroup,
  workoutId,
  supersetIndexMap,
  onSelectScheme,
  setAdvancedSchemeSearchOpen,
  onOpenApplySwap,
  errors,
  isAlternate,
  exerciseKey,
}: {
  disabled: boolean;
  superset: WorkoutSuperset | null;
  alternateGroup: WorkoutAlternateGroup | null;
  exercise: WorkoutExercise;
  workoutId: string;
  supersetIndexMap?: Map<string, number>;
  onSelectScheme: (scheme: Scheme) => void;
  setAdvancedSchemeSearchOpen: (open: boolean) => void;
  onOpenApplySwap?: (data: ApplySwapData) => void;
  errors: WorkoutPublishErrors | null;
  isAlternate: boolean;
  exerciseKey: string;
}) => {
  const {
    palette: { greyscaleLight },
  } = useTheme();
  const exerciseVideoMap = useAtomValue(exerciseVideoMapAtom);
  const videoUrl = exerciseVideoMap[exercise.libraryExerciseId];
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null);

  const id = `WorkoutExercise:${exercise.workoutExerciseId}`;
  const data: WorkoutExerciseDraggable = {
    workoutExerciseId: exercise.workoutExerciseId,
    workoutSupersetId: superset?.workoutSupersetId ?? null,
    type: 'WorkoutExercise',
    workoutSectionId: exercise.workoutSectionId,
    workoutAlternateGroupId: alternateGroup?.workoutAlternateGroupId ?? null,
    workoutId,
  };
  const { t } = useTranslation();

  const exerciseErrors = errors
    ?.get(exercise.workoutExerciseId)
    ?.map((err) => t(...err));
  const [schemeOpen, setSchemeOpen] = useState(false);

  const recentSwaps = useAtomValue(recentSwapsAtom);
  const swapData = findRecentSwap(
    recentSwaps,
    workoutId,
    exercise.libraryExerciseId,
  );
  const showApplySwapIcon =
    !!swapData &&
    !disabled &&
    !!onOpenApplySwap &&
    (swapData.hasFutureInstances || swapData.hasMatchingAthletes);

  const isFirstExerciseInAlternateGroup = !!alternateGroup && !isAlternate;
  const [collapsedExercises, setCollapsedExercises] = useAtom(
    collapsedExercisesAtom,
  );
  const isCollapsed = collapsedExercises.has(exerciseKey);

  const toggleCollapse = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCollapsedExercises((prev: Set<string>) => {
        const next = new Set(prev);
        if (next.has(exerciseKey)) {
          next.delete(exerciseKey);
        } else {
          next.add(exerciseKey);
        }
        return next;
      });
    },
    [setCollapsedExercises, exerciseKey],
  );

  const {
    attributes,
    listeners,
    setNodeRef: setDraggableRef,
    setActivatorNodeRef,
    transform,
    active,
  } = useDraggable({
    id,
    data,
    disabled,
  });
  const { isOver, setNodeRef: setDroppableRef } = useDroppable({
    id,
    data,
  });

  const [hovering, setHovering] = useState(false);

  const { showDragIcon, highlight, isDragging } = exerciseDndStyleInfo(
    active,
    hovering,
    isOver,
    data,
  );

  const showNoSchemeBadge =
    hovering &&
    exercise.sets.length === 0 &&
    !superset &&
    !disabled &&
    !alternateGroup;

  let schemeBadge: ReactNode;
  if (showNoSchemeBadge) {
    schemeBadge = (
      <NoSchemeBadge
        open={schemeOpen}
        onOpenChange={setSchemeOpen}
        onOpenAdvancedSearch={() => setAdvancedSchemeSearchOpen(true)}
        onSelect={onSelectScheme}
        libraryExerciseId={exercise.libraryExerciseId}
      />
    );
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleCollapse(e as unknown as React.MouseEvent);
    }
  };

  const content = (
    <div
      className={cn('relative h-full w-full', exerciseErrors && 'bg-red-100')}
    >
      {previewVideoUrl && (
        <div
          className="fixed inset-0"
          style={{ zIndex: 1500 }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <VideoPreviewPlayer
            videoUrl={previewVideoUrl}
            onClose={() => setPreviewVideoUrl(null)}
          />
        </div>
      )}

      {/* The draggable part */}
      <div
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        ref={setDraggableRef}
        className={blockSectionAndExerciseLabelStyle}
      >
        {showDragIcon && !schemeOpen && !disabled && (
          <div
            className="mr-1"
            {...listeners}
            {...attributes}
            ref={setActivatorNodeRef}
          >
            <MdDragIndicator size={18} color={greyscaleLight[400]} />
          </div>
        )}
        <ExerciseText exercise={exercise} isAlternate={isAlternate} />
        <div className="w-2" />
        {schemeBadge}
        {/* TODO: Probably need a superset button placeholder for text overflow? */}
        <div className="flex-1" />
        {showApplySwapIcon && swapData && (
          <Tooltip title={t('applyExerciseChangeTooltip')} arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onOpenApplySwap?.(swapData);
              }}
              sx={{ p: 0.25 }}
            >
              <MdContentCopy size={16} />
            </IconButton>
          </Tooltip>
        )}
        {superset && (
          <SupersetIcon
            workoutSupersetId={superset.workoutSupersetId}
            supersetIndex={
              supersetIndexMap?.get(superset.workoutSupersetId) ?? 0
            }
          />
        )}
        {isFirstExerciseInAlternateGroup && (
          <AlternateGroupBadge alternateGroup={alternateGroup ?? null} />
        )}
        {hovering && videoUrl && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setPreviewVideoUrl(videoUrl);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{ p: 0.25 }}
          >
            <MdPlayCircle size={16} />
          </IconButton>
        )}
        <CollapseSlotIcon
          onClick={toggleCollapse}
          onKeyDown={handleKeyDown}
          isCollapsed={isCollapsed}
          name={`${t(isCollapsed ? 'expandExercise' : 'collapseExercise', {
            exerciseName: exercise.exerciseName,
          })}`}
        />
      </div>

      {/* The draggable display */}
      {isDragging && (
        <div
          className={`${CELL_CLASSNAME_SELECTED} absolute top-0 left-0 z-30 flex h-full w-full items-center bg-white`}
          style={{ transform: CSS.Translate.toString(transform) }}
        >
          <MdDragIndicator
            size={18}
            className="mr-1"
            color={greyscaleLight[400]}
          />
          <ExerciseText exercise={exercise} isAlternate={isAlternate} />
        </div>
      )}

      {/* The droppable part */}
      <div
        className={cn(
          'absolute top-0 left-0 h-full w-full px-2.5 py-2',
          highlight && DROPPABLE_CLASSNAME,
        )}
        ref={setDroppableRef}
      ></div>
    </div>
  );

  if (exerciseErrors) {
    return (
      <Tooltip title={exerciseErrors.join(', ')} arrow>
        {content}
      </Tooltip>
    );
  }

  return content;
};

export const ExerciseText = ({
  exercise,
  isAlternate = false,
}: {
  exercise: WorkoutExercise;
  isAlternate: boolean;
}) => {
  const {
    palette: { greyscaleLight },
  } = useTheme();
  const { t } = useTranslation();

  const [showTooltip, setShowTooltip] = useState(false);
  return (
    <Tooltip title={exercise.instruction} open={showTooltip}>
      <div
        id="exercise-text"
        className={
          'overflow-hidden text-ellipsis whitespace-nowrap text-gray-800 flex items-center gap-1 font-medium'
        }
      >
        <div className="flex items-center gap-1">
          {isAlternate && <MdSubdirectoryArrowRight className="mr-1" />}
          <div className="overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
            {getExerciseDisplay(exercise, t)}
          </div>
        </div>

        {exercise.instruction && (
          <MdOutlineNote
            size={18}
            color={greyscaleLight[400]}
            onClick={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          />
        )}
      </div>
    </Tooltip>
  );
};

// Probably refactor this to a generic search component, especially because of the hacks that are needed to autoFocus
const AddExercise = ({
  disabled,
  editing,
  onBlur,
  onAdd,
  workoutSectionId,
  workoutId,
  setModalOrDrawerOpen,
  isAlternate = false,
  alternateGroup,
}: {
  disabled: boolean;
  editing: boolean;
  onBlur: () => void;
  onAdd: (exercise: BlockBuilderExercise) => void;
  workoutSectionId: string;
  workoutId: string;
  setModalOrDrawerOpen?: (open: boolean) => void;
  isAlternate?: boolean;
  alternateGroup?: WorkoutAlternateGroup | null;
}) => {
  const {
    palette: { greyscaleLight },
  } = useTheme();
  const { t } = useTranslation();
  const { team } = useViewer();
  const onCreateExercise = useCreateExercise(onAdd);
  const id = `AddWorkoutExercise:${workoutSectionId}`;

  // Advanced-search "See More" opened from the alternate picker gets the same
  // overlap-ranking + group exclusion + tag chips as the inline picker. Empty
  // (nothing sent, unchanged modal) for the primary picker. Reuses the inline
  // picker's derivation so the two never drift.
  const alternateSearchParams =
    getAlternateExerciseSearchParams(alternateGroup);
  const data: WorkoutExerciseDroppable = {
    type: 'AddWorkoutExercise',
    workoutSectionId,
    workoutId,
  };
  const { isOver, setNodeRef: setDroppableRef } = useDroppable({
    id,
    data,
  });
  const { active } = useDndContext();
  const [advancedExerciseSearch, setAdvancedExerciseSearch] =
    useState<AdvancedSearchState>({ searchTerm: '', open: false });
  const [createExerciseSheetOpen, setCreateExerciseSheetOpen] = useState(false);
  const [exerciseSearchTerm, setExerciseSearchTerm] = useState<string>('');

  if (editing && !disabled) {
    return (
      <ExerciseSearch
        onBlur={onBlur}
        onSelect={onAdd}
        alternateGroup={alternateGroup}
        onOpenAdvancedSearch={(searchTerm) => {
          setModalOrDrawerOpen?.(true);
          setAdvancedExerciseSearch({ searchTerm, open: true });
        }}
        setCreateExerciseSheetOpen={(searchTerm) => {
          setModalOrDrawerOpen?.(true);
          setCreateExerciseSheetOpen(true);
          setExerciseSearchTerm(searchTerm);
        }}
      />
    );
  }

  const { showDragIcon, highlight } = exerciseDndStyleInfo(
    active,
    false,
    isOver,
    data,
  );

  return (
    <span
      className={cn(
        'flex px-2.5 py-2',
        highlight ? DROPPABLE_CLASSNAME : 'text-gray-500',
      )}
      ref={setDroppableRef}
    >
      {showDragIcon && (
        <div className="mr-1">
          <MdDragIndicator size={18} color={greyscaleLight[400]} />
        </div>
      )}
      <div className="flex items-center">
        {isAlternate && <MdSubdirectoryArrowRight className="mr-1" />}
        {isAlternate ? t('addAlternateExercise') : t('addExercise')}
      </div>
      {advancedExerciseSearch.open && (
        <AdvancedExerciseSearchModal
          id={id}
          open={advancedExerciseSearch.open}
          onClose={() => {
            setModalOrDrawerOpen?.(false);
            setAdvancedExerciseSearch({ searchTerm: '', open: false });
          }}
          setOpenCreateExerciseSheet={(searchTerm) => {
            setCreateExerciseSheetOpen(true);
            setAdvancedExerciseSearch({ searchTerm: '', open: false });
            setExerciseSearchTerm(searchTerm);
          }}
          initialSearchTerm={advancedExerciseSearch.searchTerm}
          onAddExercise={(exercise) => {
            onAdd(exercise);
            setAdvancedExerciseSearch({ searchTerm: '', open: false });
            setModalOrDrawerOpen?.(false);
          }}
          teamId={team.selectedTeam}
          baseExerciseId={alternateSearchParams.baseExerciseId}
          excludeExerciseIds={alternateSearchParams.excludeExerciseIds}
        />
      )}
      <CreateEditExerciseSheet
        open={createExerciseSheetOpen}
        onOpenChange={() => {
          setModalOrDrawerOpen?.(false);
          setCreateExerciseSheetOpen(false);
          setExerciseSearchTerm('');
        }}
        onSubmit={onCreateExercise}
        defaultValues={{ name: exerciseSearchTerm }}
        teamId={team.selectedTeam}
        showTeamName={true}
      />
    </span>
  );
};

// Stable component identity for the Autocomplete's Paper slot. Defining this
// inline in the render made it a new component type every render, so MUI
// unmounted/remounted the listbox (and reset its scroll to the top) on every
// re-render, e.g. the hover-driven re-renders while scrolling the results.
// The dynamic footer (See More / Create Exercise) is passed in via slotProps so
// this component type stays constant and MUI re-renders it in place. (SC-3907)
type ExerciseSearchResultsPaperProps = {
  children?: ReactNode;
  footer?: ReactNode;
};

const ExerciseSearchResultsPaper = ({
  children,
  footer,
}: ExerciseSearchResultsPaperProps) => (
  <Paper sx={{ paddingTop: 0, marginTop: 1 }}>
    {children}
    {footer}
  </Paper>
);

// Probably refactor this to a generic search component, especially because of the hacks that are needed to autoFocus
export const ExerciseSearch = ({
  onBlur,
  onSelect,
  onOpenAdvancedSearch,
  setCreateExerciseSheetOpen,
  alternateGroup,
}: {
  onBlur: () => void;
  onSelect: (exercise: BlockBuilderExercise) => void;
  onOpenAdvancedSearch: (term: string) => void;
  setCreateExerciseSheetOpen: (searchTerm: string) => void;
  alternateGroup?: WorkoutAlternateGroup | null;
}) => {
  // Read inputAtom once to pre-fill the box (slots write it on edit start);
  // filtering then runs off local state so keystrokes don't re-render the
  // sheet through the shared atom.
  const [searchTerm, setSearchTerm] = useState(() => jotaiStore.get(inputAtom));
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null);
  const {
    org: {
      settings: { enableTeamLibrary },
    },
    team: { selectedTeam },
    hasPermissions,
  } = useViewer();
  const canCreateExercise = hasPermissions('library.exercises', 'create');
  const { t, i18n } = useTranslation();

  // Overlap ranking + group exclusion + inline tag chips are alternate-picker
  // only; both derive from the alternate group the picker opened for. Empty
  // (nothing sent, no chips) everywhere else.
  const alternateSearchParams = useMemo(
    () => getAlternateExerciseSearchParams(alternateGroup),
    [alternateGroup],
  );
  const showTags = !!alternateSearchParams.baseExerciseId;

  // Shares the preloaded list's cache entry (see useBlockExercises), so this
  // is only a network fetch when the picker opens before the preload finished.
  const allExercisesResult = useBlockExercises();

  const exercises = useMemo(
    () =>
      rankExercises(
        allExercisesResult.data ?? [],
        searchTerm,
        alternateSearchParams,
        // Mirrors the server's language pick for stop-word filtering
        i18n.language.split('-')[0],
      ).slice(0, 8),
    [allExercisesResult.data, searchTerm, alternateSearchParams, i18n.language],
  );

  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const updateHighlightedIndex = (type: 'increment' | 'decrement') => {
    setHighlightedIndex((i) => {
      if (i === null) {
        return 0;
      } else {
        const diff = type === 'decrement' ? -1 : 1;
        return (i + diff + exercises.length) % exercises.length;
      }
    });
  };

  return (
    <>
      {previewVideoUrl && (
        <div
          className="fixed inset-0"
          style={{ zIndex: 1500 }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <VideoPreviewPlayer
            videoUrl={previewVideoUrl}
            onClose={() => setPreviewVideoUrl(null)}
          />
        </div>
      )}
      <Autocomplete
        // Keep the autocomplete open if the exercise search has focus
        open={true}
        // https://mui.com/material-ui/react-autocomplete/#search-as-you-type
        filterOptions={(x) => x}
        disableCloseOnSelect
        renderInput={(params: Omit<AutocompleteRenderInputParams, 'size'>) => (
          <div
            ref={params.InputProps.ref}
            className="h-full min-w-full max-w-0"
          >
            <input
              type="text"
              {...params.inputProps}
              className="h-full min-w-full max-w-0 outline-none"
              autoFocus
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setHighlightedIndex(null);
              }}
              onBlur={() => {
                if (!previewVideoUrl) onBlur();
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  updateHighlightedIndex('decrement');
                } else if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  updateHighlightedIndex('increment');
                } else if (e.key === 'Enter') {
                  const highlightedExercise =
                    highlightedIndex !== null && exercises[highlightedIndex];
                  if (highlightedExercise) {
                    trackEvent('Strength | Block Builder | Add Exercise', {
                      libraryExerciseId: highlightedExercise.id,
                    });
                    onSelect(highlightedExercise);
                  }
                }
              }}
            />
          </div>
        )}
        disablePortal
        openOnFocus
        sx={{
          width: '100%',
          height: '100%',
        }}
        getOptionLabel={(option) => option.name}
        options={exercises}
        // Shown while the preloaded library list is still fetching
        loading={allExercisesResult.isLoading}
        loadingText={t('loading')}
        PaperComponent={ExerciseSearchResultsPaper}
        slotProps={{
          // Footer is passed as a prop (not rendered by an inline PaperComponent)
          // so the Paper component type stays stable and MUI doesn't remount the
          // listbox / reset its scroll on re-render. (SC-3907)
          paper: {
            footer: (
              <>
                {exercises.length > 0 && (
                  <AdvancedSearchListItem
                    highlighted={exercises.length === highlightedIndex}
                    onHover={() => setHighlightedIndex(exercises.length)}
                    onClick={() => {
                      trackEvent(
                        'Strength | Block Builder | Advanced Exercise Search',
                        {
                          searchTerm,
                        },
                      );
                      onOpenAdvancedSearch(searchTerm);
                    }}
                  />
                )}
                <DisabledWithTooltip
                  disabled={!canCreateExercise}
                  reason={t('permissionDisabled.create', {
                    item: t('exercises'),
                  })}
                >
                  <CreateExerciseListItem
                    highlighted={
                      canCreateExercise &&
                      exercises.length + 1 === highlightedIndex
                    }
                    onHover={() => setHighlightedIndex(exercises.length + 1)}
                    onClick={() => setCreateExerciseSheetOpen(searchTerm)}
                  />
                </DisabledWithTooltip>
              </>
            ),
          } as ExerciseSearchResultsPaperProps,
        }}
        renderOption={(props, exercise, { index }) => (
          <ExerciseListItem
            key={exercise.id}
            exercise={exercise}
            highlighted={index === highlightedIndex}
            onClick={() => onSelect(exercise)}
            onHover={() => setHighlightedIndex(index)}
            onPlay={setPreviewVideoUrl}
            showTeamName={enableTeamLibrary}
            showTags={showTags}
          />
        )}
      />
    </>
  );
};

const ExerciseSearchVideoThumbnail = ({
  videoUrl,
  onPlay,
}: {
  videoUrl: string;
  onPlay: (videoUrl: string) => void;
}) => {
  const isYoutube = !!getYoutubeVideoId(videoUrl);
  const thumbnailUrl = isYoutube
    ? getYoutubePreviewThumbnail(videoUrl)
    : undefined;

  return (
    <div
      className="relative flex-shrink-0 rounded overflow-hidden bg-black cursor-pointer transition-opacity hover:opacity-80 w-10 h-6"
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onPlay(videoUrl);
      }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {isYoutube && thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full bg-gray-700" />
      )}
      <div
        className="pointer-events-none absolute inset-0 bg-black/45"
        aria-hidden
      />
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <MdPlayCircle size={16} color="white" />
      </div>
    </div>
  );
};

const ExerciseListItem = ({
  exercise,
  onClick,
  onHover,
  onPlay,
  highlighted,
  showTeamName,
  showTags = false,
}: {
  exercise: BlockBuilderExercise;
  onClick: () => void;
  highlighted: boolean;
  onHover: () => void;
  onPlay?: (videoUrl: string) => void;
  showTeamName?: boolean;
  showTags?: boolean;
}) => {
  const {
    palette: { mediumEmphasisTextLight },
  } = useTheme();
  const { t } = useTranslation();

  const teamName = exercise.team?.name ?? t('baseLibrary');

  return (
    <div
      className={cn(
        'cursor-pointer p-2',
        highlighted ? 'bg-gray-100' : '',
        'flex flex-row items-center gap-2',
        'text-sm',
      )}
      onMouseDown={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onMouseEnter={onHover}
    >
      {onPlay && exercise.videoUrl && (
        <ExerciseSearchVideoThumbnail
          videoUrl={exercise.videoUrl}
          onPlay={onPlay}
        />
      )}
      <div className={cn('flex flex-col', showTags && 'min-w-0 flex-1 gap-1')}>
        <span>{exercise.name}</span>
        {showTeamName && (
          <span style={{ color: mediumEmphasisTextLight }} className="text-xs">
            {teamName}
          </span>
        )}
        {showTags && <ExerciseTagChips tags={exercise.tags} />}
      </div>
    </div>
  );
};

const CreateExerciseListItem = ({
  onClick,
  highlighted,
  onHover,
  disabled = false,
  'aria-describedby': describedBy,
}: {
  highlighted: boolean;
  onClick: () => void;
  onHover: () => void;
  disabled?: boolean;
  'aria-describedby'?: string;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <button
        type="button"
        key={'create-exercise'}
        aria-describedby={describedBy}
        className={cn(
          'flex w-full flex-row items-center gap-1 p-2 text-left text-xs text-black-300',
          highlighted ? 'bg-gray-100' : '',
          disabled ? 'cursor-default opacity-50' : 'cursor-pointer',
        )}
        onMouseDown={
          disabled
            ? undefined
            : (e) => {
                e.stopPropagation();
                onClick();
              }
        }
        onMouseEnter={disabled ? undefined : onHover}
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
      >
        <MdAdd size={15} color="grey" />
        {t('createExercise')}
      </button>
    </>
  );
};

const AdvancedSearchListItem = ({
  onHover,
  highlighted,
  onClick,
}: {
  highlighted: boolean;
  onClick: () => void;
  onHover: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <div
      key={'see-more'}
      className={cn(
        'flex flex-row items-center gap-1 p-2 text-xs text-black-300 cursor-pointer',
        highlighted ? 'bg-gray-100' : '',
      )}
      onMouseDown={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onMouseEnter={onHover}
    >
      <MdToday size={15} color="grey" />
      {t('seeMore')}
    </div>
  );
};

const NoSchemeBadge = ({
  open,
  onOpenChange,
  onOpenAdvancedSearch,
  onSelect,
  libraryExerciseId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenAdvancedSearch: () => void;
  onSelect: (scheme: Scheme) => void;
  libraryExerciseId: string;
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    onOpenChange(true);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    onOpenChange(false);
  };

  const libraryExerciseQuery = trpc.exerciseLibrary.exerciseById.useQuery(
    {
      libraryExerciseId,
    },
    {
      staleTime: 60_000,
    },
  );
  const schemeQuery = trpc.schemeLibrary.searchSchemes.useQuery(
    {
      trainingTypeId: libraryExerciseQuery.data?.trainingTypeId,
    },
    {
      staleTime: 60_000,
    },
  );

  const filteredSchemes = schemeQuery.data?.records?.slice(0, 5) ?? [];

  return (
    <>
      <Button
        onClick={handleClick}
        aria-describedby={'scheme-popover'}
        className="rounded-full"
        variant="outlined"
        sx={{
          display: 'flex',
          borderRadius: '9999px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: '12px',
          fontWeight: 500,
          paddingX: '12px',
          height: '18px',
        }}
        size="small"
      >
        {t('scheme')}
        <MdKeyboardArrowDown size={15} className="ml-1" />
      </Button>
      <Popover
        open={Boolean(
          open &&
          schemeQuery.data &&
          libraryExerciseQuery.data &&
          Boolean(anchorEl),
        )}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {filteredSchemes.map((scheme) => (
          <div
            key={scheme.id}
            className="cursor-pointer p-2 hover:bg-gray-100 text-sm"
            onClick={() => {
              onSelect(scheme);
              onOpenChange(false);
            }}
          >
            <p>{scheme.name}</p>
            <p className="text-xs text-gray-500">
              {t('weekCount', {
                count: new Set(scheme.sets.map((set) => set.cycleIndex)).size,
              })}
            </p>
          </div>
        ))}
        <AdvancedSearchListItem
          highlighted={false}
          onHover={() => {}}
          onClick={onOpenAdvancedSearch}
        />
        {filteredSchemes?.length === 0 && (
          <div className="p-2">
            <p className="text-xs">{t('noSchemesFoundForDefaultType')}</p>
          </div>
        )}
        {schemeQuery.error || libraryExerciseQuery.error ? (
          <div className="p-2 text-red-500 flex items-center justify-center">
            {t('somethingWentWrong')}
          </div>
        ) : null}
      </Popover>
    </>
  );
};

export function useCreateExercise(
  onSuccess: (exercise: BlockBuilderExercise) => void,
) {
  const createExercise = trpc.exerciseLibrary.createExercise.useMutation({});
  const utils = trpc.useUtils();
  const { t } = useTranslation();
  const {
    team: { selectedTeam },
  } = useViewer();

  return ({ exercise }: { exercise: ExerciseForm }) => {
    const newExerciseId = v4();
    createExercise.mutate(
      createExerciseInput(newExerciseId, exercise, selectedTeam),
      {
        onSuccess: async () => {
          toast({
            title: t('exerciseCreated', { name: exercise.name }),
            severity: 'success',
          });
          const newExercise = await utils.exerciseLibrary.exerciseById.fetch({
            libraryExerciseId: newExerciseId,
          });
          if (newExercise) {
            // Add to the preloaded library list so the pickers find it without
            // refetching (same key as useBlockExercises). If the preload is
            // still in flight there is no cache entry to update — invalidate
            // instead so the in-flight snapshot can't hide the new exercise.
            const preloadedInput = { teamId: selectedTeam };
            const cached =
              utils.exerciseLibrary.blockBuilderExercises.getData(
                preloadedInput,
              );
            if (cached) {
              utils.exerciseLibrary.blockBuilderExercises.setData(
                preloadedInput,
                (old) =>
                  old &&
                  [...old, newExercise].sort((a, b) =>
                    a.name.localeCompare(b.name),
                  ),
              );
            } else {
              utils.exerciseLibrary.blockBuilderExercises.invalidate(
                preloadedInput,
              );
            }
            onSuccess(newExercise);
          }
        },
        onError: (error) =>
          toast({
            title: error.message,
            severity: 'error',
          }),
      },
    );
  };
}

const AlternateGroupBadge = ({
  alternateGroup,
}: {
  alternateGroup: WorkoutAlternateGroup;
}) => {
  const numAlternates = alternateGroup.exercises.length - 1;
  return numAlternates > 0 ? (
    <div className="flex items-center gap-1 rounded-full bg-informational-25 text-informational-700 px-2 text-[11px]">
      {numAlternates}
      <MdForkRight size={12} fontWeight="bold" />
    </div>
  ) : null;
};

export function AddSetButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <Button
      sx={({ palette: { greyscaleDark, greyscaleLight } }: Theme) => ({
        color: greyscaleDark[300],
        fontWeight: 500,
        height: '100%',
        width: '100%',
        borderRadius: 0,
        backgroundColor: greyscaleLight[100],
        '&:hover': {
          backgroundColor: 'white',
        },
      })}
      onClick={onClick}
    >
      {t('addSetPlus')}
    </Button>
  );
}
